import React, { useEffect, useState, useMemo } from "react";
import "./MessageTemplate.css";
import { DeleteIcon, EditIcon, SearchIcon } from "../../../icons/reactIcons";
import StatusChip from "../../../components/custom-statusChip/StatusChip";
import Table from "../../../components/reusable-table/Table";
import Input from "../../../components/reusable-input/Input";
import MessageTemplateModal from "./modals/MessageTemplateModal";
import { useAppDispatch, useAppSelector } from "../../../redux/store/hooks";
import {
  fetchMessageTemplateListThunk,
  createMessageTemplateThunk,
  updateMessageTemplateThunk,
} from "../../../redux/slices/messageTemplateSlice";
import { formatDateTime } from "../../../utils/formatDateTime";
import { CreateMessageTemplatePayload } from "../../../apis/messageTemplateApis";
import { fetchActivityListThunk } from "../../../redux/slices/activitySlice";
import ConfirmUserActionModal from "../../../components/confirm-modal/ConfirmUserActionModal";
import { formatRole } from "../../../utils/formatRole";
import Loader from "../../../components/custom-loader/Loader";

interface Template {
  id: string;
  title: string;
  desc: string;
  metric: string;
  condition: string;
  value: number;
  time: string;
  timeType: string;
  status: "Active" | "Inactive";
  createdBy: string;
  updatedOn: string;
  updatedBy: string;
  createdOn: string;
}

const columns = [
  { header: "Message", accessor: "message" },
  { header: "Status", accessor: "status" },
  { header: "Created By", accessor: "createdBy" },
  { header: "Updated By", accessor: "updatedBy" },
];

const MessageTemplate: React.FC = () => {
  const dispatch = useAppDispatch();
  const { templates, loading } = useAppSelector(
    (state) => state.messageTemplate,
  );
  const { activityList, loading: activityLoading } = useAppSelector(
    (state) => state.activity,
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(
    null,
  );
  const [submitting, setSubmitting] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchMessageTemplateListThunk());
    dispatch(fetchActivityListThunk());
  }, [dispatch]);

  const getMetricName = (metricId: string) => {
    // String conversion ensures comparison works even if one is an object/number
    return (
      activityList.find((a) => String(a.id) === String(metricId))
        ?.activityName || "-"
    );
  };

  const getConditionSymbol = (cond: string) => {
    switch (cond) {
      case "Greater Than":
        return ">";
      case "Less Than":
        return "<";
      case "Equal To":
        return "=";
      default:
        return cond;
    }
  };

  const handleCreate = () => {
    setEditingTemplate(null);
    setOpenModal(true);
  };

  const handleEdit = (row: Template) => {
    setEditingTemplate(row);
    setOpenModal(true);
  };

  const handleDelete = (row: Template) => {
    setSelectedTemplate(row);
    setOpenDeleteModal(true);
  };

  const handleSubmit = async (formData: any) => {
    setBtnLoading(true);
    const basePayload = {
      messageTitle: formData.title,
      messageDescription: formData.body,
      messageMetric: formData.metric,
      messageCondition: formData.condition,
      messageValue: Number(formData.value),
      messageTriggerType: formData.timeType,
      messageTriggerTime: formData.time,
      messageTriggerTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    if (editingTemplate) {
      const updatePayload = {
        id: editingTemplate.id,
        ...basePayload,
        isActive: formData.status === "Active", // ✅ switch control
        isDeleted: false,
      };

      const resultAction = await dispatch(
        updateMessageTemplateThunk(updatePayload),
      );

      if (updateMessageTemplateThunk.fulfilled.match(resultAction)) {
        setOpenModal(false);
        setEditingTemplate(null);
        dispatch(fetchMessageTemplateListThunk());
      }
    } else {
      const resultAction = await dispatch(
        createMessageTemplateThunk(basePayload),
      );

      if (createMessageTemplateThunk.fulfilled.match(resultAction)) {
        setOpenModal(false);
      }
    }
    setBtnLoading(false);
  };

  // useMemo ensures that when activityList is finally fetched, the table re-renders
  const formattedData = useMemo(() => {
    return templates
      .filter((item) =>
        item.messageTitle.toLowerCase().includes(searchTerm.toLowerCase()),
      )
      .map((item) => {
        const template: Template = {
          id: item.id,
          title: item.messageTitle,
          desc: item.messageDescription,
          metric: item.messageMetric,
          condition: item.messageCondition,
          value: item.messageValue,
          time: item.messageTriggerTime?.slice(0, 5),
          timeType: item.messageTriggerType,
          status: item.isActive ? "Active" : "Inactive",
          createdBy: item.creator?.name || "-",
          updatedBy: item.updater?.name || "-",
          createdOn: item.createdAt,
          updatedOn: item.updatedAt,
        };

        return {
          original: template,
          message: (
            <div className="msg-cell">
              <strong>{template.title}</strong>
              <div className="metric">
                {/* Now dynamically updates when activityList loads */}
                {formatRole(getMetricName(template.metric))}{" "}
                {getConditionSymbol(template.condition)} {template.value}{" "}
                {template.timeType} {template.time}
              </div>
              <div className="desc">{template.desc}</div>
            </div>
          ),
          status: (
            <StatusChip
              status={template.status}
              color={template.status === "Active" ? "#2E7D32" : "#B23C17"}
              backgroundcolor={
                template.status === "Active" ? "#E8F5E9" : "#FBE9E7"
              }
            />
          ),
          updatedBy: (
            <div className="merged-cell">
              <div className="merged-label">{template.updatedBy}</div>
              <div className="merged-date">
                {formatDateTime(template.updatedOn)}
              </div>
            </div>
          ),
          createdBy: (
            <div className="merged-cell">
              <div className="merged-label">{template.createdBy}</div>
              <div className="merged-date">
                {formatDateTime(template.createdOn)}
              </div>
            </div>
          ),
        };
      });
  }, [templates, activityList, searchTerm]);

  const actions = [
    {
      label: <DeleteIcon color={"black"} />,
      onClick: (row: any) => handleDelete(row.original),
      className: "delete-btn",
    },
    {
      label: <EditIcon color={"black"} />,
      onClick: (row: any) => handleEdit(row.original),
      className: "edit-btn",
    },
  ];

  const handleDeleteQuestion = async () => {
    if (!selectedTemplate) return;

    setSubmitting(true);

    const deletePayload = {
      id: selectedTemplate.id,
      messageTitle: selectedTemplate.title,
      messageDescription: selectedTemplate.desc,
      messageMetric: selectedTemplate.metric,
      messageCondition: selectedTemplate.condition,
      messageValue: selectedTemplate.value,
      messageTriggerType: selectedTemplate.timeType,
      messageTriggerTime: selectedTemplate.time,
      messageTriggerTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      isActive: false,
      isDeleted: true,
    };

    const resultAction = await dispatch(
      updateMessageTemplateThunk(deletePayload),
    );

    if (updateMessageTemplateThunk.fulfilled.match(resultAction)) {
      setOpenDeleteModal(false);
      setSelectedTemplate(null);
      dispatch(fetchMessageTemplateListThunk());
    }

    setSubmitting(false);
  };

  if (loading && activityLoading) return <Loader />;

  return (
    <div className="message-template">
      <div className="message-template-header">
        <h2 className="page-title">Message Template</h2>
        <div style={{ display: "flex", alignItems: "center", gap: "1.5rem" }}>
          <Input
            type="text"
            placeholder="Search"
            onChange={(e) => setSearchTerm(e.target.value)}
            value={searchTerm}
            search={true}
            startIcon={<SearchIcon fontSize={"20px"} />}
            removeBottomMargin
          />
          <button className="user-groups-btn" onClick={handleCreate}>
            Create New
          </button>
        </div>
      </div>

      <Table
        columns={columns}
        data={formattedData}
        actions={actions}
        rowsPerPage={15}
        rounded={true}
      />

      {openModal && (
        <MessageTemplateModal
          open={openModal}
          onClose={() => setOpenModal(false)}
          onSubmit={handleSubmit}
          initialData={editingTemplate || undefined}
          metrics={activityList}
          loading={btnLoading}
        />
      )}

      {openDeleteModal && (
        <ConfirmUserActionModal
          type="delete"
          userName={selectedTemplate?.title || ""}
          loading={submitting}
          onClose={() => setOpenDeleteModal(false)}
          onConfirm={handleDeleteQuestion}
        />
      )}
    </div>
  );
};

export default MessageTemplate;
